import { useMutation, useQuery } from "@apollo/client";
import {
    AppShell,
    Burger,
    createStyles,
    Header,
    MediaQuery,
    Menu,
    Navbar,
    Text,
    useMantineTheme,
} from "@mantine/core";
import { useMediaQuery, useViewportSize } from "@mantine/hooks";
import { showNotification } from "@mantine/notifications";
import { IconChevronDown, IconLogout, IconUserCircle } from "@tabler/icons";
import { LOGOUT } from "apollo/mutuations";
import { AUTH } from "apollo/queries";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import NavbarSimple from "../navigation";

const useStyles = createStyles((theme, _params, getRef) => {
  const icon = getRef("icon");
  return {
    link: {
      ...theme.fn.focusStyles(),
      display: "flex",
      alignItems: "center",
      textDecoration: "none",
      fontSize: theme.fontSizes.sm,
      color:
        theme.colorScheme === "dark" ? theme.colors.dark[1] : "rgb(20, 61, 89)",
      padding: `${theme.spacing.xs}px ${theme.spacing.sm}px`,
      borderRadius: theme.radius.sm,
      fontWeight: 600,
    },

    linkIcon: {
      ref: icon,
      color: theme.colorScheme === "dark" ? theme.colors.dark[2] : "#FFFFFF",
      marginRight: theme.spacing.sm,
    },

    linkActive: {
      "&, &:hover": {
        backgroundColor: "#FF6A00",
        color: "#FFFFFF",
        [`& .${icon}`]: {
          color: "#FFFFFF",
        },
      },
    },

    // New styles for dropdown menu
    userMenu: {
      color: "#FFFFFF",
      fontWeight: 500,
    },
  };
});

const Layout = ({ children, setPosition }) => {
  const { classes } = useStyles();
  const roles = JSON.parse(localStorage.getItem("roles")) || [];
  const hasSupplierOrManagerPermission = roles.some(
    (permission) =>
      permission === "supplier" || permission === "warehouse_manager",
  );
  const [signout] = useMutation(LOGOUT);
  const navigate = useNavigate();

  const { width } = useViewportSize();
  const largeScreen = useMediaQuery("(min-width: 800px)");

  const theme = useMantineTheme();
  const [opened, setOpened] = useState(true);
  const { data, loading: authLoading, refetch } = useQuery(AUTH, {});

  useEffect(() => {
    if (data) {
      localStorage.setItem("user", JSON.stringify(data.auth));
    }
  }, [data]);

  useEffect(() => {
    refetch();
  }, []);

  const logout = () => {
    signout({
      onCompleted() {
        localStorage.removeItem("auth_token");
        localStorage.removeItem("permissions");
        localStorage.removeItem("roles");
        navigate("/login");
        showNotification({
          color: "green",
          title: "Success",
          message: "Logged Out!",
        });
      },
      onError(err) {
        showNotification({
          color: "red",
          title: "Error",
          message: "Something went wrong!",
        });
      },
    });
  };

  const goToProfile = () => {
    navigate("/profile"); // Adjust this path to your profile page route
  };

  return (
    <AppShell
      styles={{
        main: {
          paddingLeft: opened ? width / 5 : width / 14,
          background:
            theme.colorScheme === "dark"
              ? theme.colors.dark[8]
              : theme.colors.gray[0],
          transition: "all .2s ease-in",
        },
      }}
      navbarOffsetBreakpoint="sm"
      asideOffsetBreakpoint="sm"
      navbar={
        <Navbar
          p="md"
          hiddenBreakpoint="sm"
          hidden={!opened}
          style={{ maxWidth: width / 14 }}
        >
          <NavbarSimple
            opened={opened}
            setOpened={setOpened}
            setPosition={setPosition}
          />
        </Navbar>
      }
      header={
        !largeScreen ? (
          <Header height={70} p="md">
            <div
              style={{ display: "flex", alignItems: "center", height: "100%" }}
            >
              <MediaQuery largerThan="sm" styles={{ display: "none" }}>
                <Burger
                  opened={opened}
                  onClick={() => setOpened((o) => !o)}
                  size="sm"
                  color={theme.colors.gray[6]}
                  mr="xl"
                />
              </MediaQuery>
            </div>
          </Header>
        ) : (
          <div
            style={{
              position: "sticky",
              top: 0,
              zIndex: 1000,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Header
              style={{
                color: "#FFFFFF",
                backgroundColor: hasSupplierOrManagerPermission
                  ? "#1B91DF"
                  : "#FF7519",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
              height={70}
              p="md"
            >
              <Text fw={400} size="xl" style={{ marginLeft: "15px" }}>
                {hasSupplierOrManagerPermission
                  ? `ANT B2B Supplier`
                  : `ANT B2B Admin`}
              </Text>
              <Burger
                style={{ marginLeft: "80px", paddingLeft: "100px" }}
                opened={false}
                onClick={() => setOpened((o) => !o)}
                size="md"
                color={"#FFFFFF"}
                mr="auto"
                ml={opened ? 0 : "sm"}
              />

              {/* User Dropdown Menu */}
              <Menu
                position="bottom-end"
                withArrow
                shadow="md"
                width={200}
                classNames={{ dropdown: classes.userMenu }}
              >
                <Menu.Target>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "8px 12px",
                      borderRadius: "8px",
                      cursor: "pointer",
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                      transition: "background-color 0.2s",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.2)",
                      },
                    }}
                  >
                    <Text size="sm" className={classes.userMenu}>
                      {data?.auth.name}
                    </Text>
                    <IconChevronDown size={16} />
                  </div>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    icon={<IconUserCircle size={18} />}
                    onClick={goToProfile}
                  >
                    Profile
                  </Menu.Item>
                  <Menu.Divider />
                  <Menu.Item
                    icon={<IconLogout size={18} />}
                    onClick={logout}
                    color="red"
                  >
                    Logout
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Header>
          </div>
        )
      }
    >
      {children}
    </AppShell>
  );
};

export default Layout;
